import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useState } from 'react';
import useEventCallback from '@restart/hooks/useEventCallback';
import useDebouncedCallback from '@restart/hooks/useDebouncedCallback';
import LegalsContext from '../LegalsContext';
import fetchLegals from '../utils/fetchLegals';
import createLegalsMap from '../utils/createLegalsMap';
/**
 * Provider used to handle the fetching and storing of legals. By default, it will
 * fetch legals from telus.com but you can override this with any custom endpoint.
 * See the `url` prop definition for more details.
 *
 * If multiple `LegalsProvider`s are present in the React tree, then the children
 * `LegalsProvider`s will pull data from the parent if there is one available. Note,
 * that if there are sibling providers and no parent, legal numbering will be
 * duplicated.
 *
 */
const LegalsProvider = ({ children, url = 'https://www.telus.com/api/cf/legals', copy = 'en', locale = 'en-CA', preview = false, footnoteComponent: FootnoteComponent }) => {
    const parentContext = useContext(LegalsContext);
    const [legalsMap, setLegalsMap] = useState({});
    const [indexedLegalSlugs, setIndexedLegalSlugs] = useState([]);
    const [nonIndexedLegalSlugs, setNonIndexedLegalSlugs] = useState([]);
    const [activeFootnoteNumber, setActiveFootnoteNumber] = useState(null);
    // Debounce in case we have rapid changes in legals during render.
    const debouncedFetchLegals = useDebouncedCallback((slugs) => {
        fetchLegals(url, slugs, locale, preview).then((legals) => {
            const map = createLegalsMap(legals);
            setLegalsMap((oldMap) => (Object.assign(Object.assign({}, oldMap), map)));
        });
    }, 300);
    useEffect(() => {
        const slugs = [...indexedLegalSlugs, ...nonIndexedLegalSlugs];
        debouncedFetchLegals(slugs);
    }, [indexedLegalSlugs, nonIndexedLegalSlugs, locale, preview]);
    const addIndexedSlugs = useEventCallback((slugs) => {
        setIndexedLegalSlugs((oldSlugs) => Array.from(new Set([...oldSlugs, ...slugs])));
    });
    const addNonIndexedSlugs = useEventCallback((slugs) => {
        setNonIndexedLegalSlugs((oldSlugs) => Array.from(new Set([...oldSlugs, ...slugs])));
    });
    const onClickFootnoteLink = useEventCallback((number) => {
        setActiveFootnoteNumber(number);
    });
    const handleFootnoteClick = useEventCallback(() => {
        setActiveFootnoteNumber(null);
    });
    useEffect(() => {
        const handleAddIndexedSlugs = (event) => {
            addIndexedSlugs(event.detail);
        };
        const handleAddNonIndexedSlugs = (event) => {
            addNonIndexedSlugs(event.detail);
        };
        const handleClickFootnoteLink = (event) => {
            onClickFootnoteLink(event.detail);
        };
        window.addEventListener('telus.legalsprovider.addindexedslugs', handleAddIndexedSlugs);
        window.addEventListener('telus.legalsprovider.addnonindexedslugs', handleAddNonIndexedSlugs);
        window.addEventListener('telus.legalsprovider.clickfootnotelink', handleClickFootnoteLink);
        return () => {
            window.removeEventListener('telus.legalsprovider.addindexedslugs', handleAddIndexedSlugs);
            window.removeEventListener('telus.legalsprovider.addnonindexedslugs', handleAddNonIndexedSlugs);
            window.removeEventListener('telus.legalsprovider.clickfootnotelink', handleClickFootnoteLink);
        };
    }, []);
    useEffect(() => {
        const event = new CustomEvent('telus.legalsprovider.data', {
            detail: {
                copy,
                indexedLegalSlugs,
                nonIndexedLegalSlugs,
                legalsMap
            }
        });
        window.dispatchEvent(event);
    }, [copy, indexedLegalSlugs, nonIndexedLegalSlugs, legalsMap]);
    const contextValue = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g;
        return ({
            addIndexedSlugs: (_a = parentContext === null || parentContext === void 0 ? void 0 : parentContext.addIndexedSlugs) !== null && _a !== void 0 ? _a : addIndexedSlugs,
            addNonIndexedSlugs: (_b = parentContext === null || parentContext === void 0 ? void 0 : parentContext.addNonIndexedSlugs) !== null && _b !== void 0 ? _b : addNonIndexedSlugs,
            copy: (_c = parentContext === null || parentContext === void 0 ? void 0 : parentContext.copy) !== null && _c !== void 0 ? _c : copy,
            indexedLegalSlugs: (_d = parentContext === null || parentContext === void 0 ? void 0 : parentContext.indexedLegalSlugs) !== null && _d !== void 0 ? _d : indexedLegalSlugs,
            nonIndexedLegalSlugs: (_e = parentContext === null || parentContext === void 0 ? void 0 : parentContext.nonIndexedLegalSlugs) !== null && _e !== void 0 ? _e : nonIndexedLegalSlugs,
            legalsMap: (_f = parentContext === null || parentContext === void 0 ? void 0 : parentContext.legalsMap) !== null && _f !== void 0 ? _f : legalsMap,
            onClickFootnoteLink: (_g = parentContext === null || parentContext === void 0 ? void 0 : parentContext.onClickFootnoteLink) !== null && _g !== void 0 ? _g : onClickFootnoteLink
        });
    }, [
        parentContext,
        addIndexedSlugs,
        addNonIndexedSlugs,
        copy,
        indexedLegalSlugs,
        nonIndexedLegalSlugs,
        legalsMap,
        onClickFootnoteLink
    ]);
    const activeLegal = activeFootnoteNumber !== null ? legalsMap[indexedLegalSlugs[activeFootnoteNumber - 1]] : null;
    return (_jsxs(LegalsContext.Provider, { value: contextValue, children: [children, _jsx(FootnoteComponent, { copy: copy, content: activeLegal === null || activeLegal === void 0 ? void 0 : activeLegal.body, number: activeFootnoteNumber, isOpen: !!(activeLegal === null || activeLegal === void 0 ? void 0 : activeLegal.body), onClose: handleFootnoteClick })] }));
};
LegalsProvider.displayName = 'LegalsProvider';
export function useLegalsContext() {
    const context = useContext(LegalsContext);
    if (context === undefined) {
        throw new Error('You must wrap your application in a LegalsProvider');
    }
    return context;
}
export default LegalsProvider;
